/*
 * Unified search logic for Gist Yarn.
 * Central authority for yarn line matching, bundle filtering, query enhancement,
 * and DOM injection across all search paths (predictive, full results, resource).
 *
 * Depends on: search-enhancer.js (must load first — both use defer, document order guarantees this)
 * Data source: #gist-yarn-lines JSON block rendered by snippets/yarn-line-data.liquid
 */
(function() {
  'use strict';

  var yarnLines = null;

  function getYarnLines() {
    if (yarnLines) return yarnLines;
    var el = document.getElementById('gist-yarn-lines');
    if (!el) return [];
    try { yarnLines = JSON.parse(el.textContent); } catch (e) { yarnLines = []; }
    return yarnLines;
  }

  /**
   * Test if a keyword appears in a query string.
   * Multi-word keywords use simple indexOf.
   * Single words and those with special chars (like "8/2") use indexOf
   * with boundary checks to avoid partial matches.
   */
  function keywordMatches(queryLower, kw) {
    var idx = queryLower.indexOf(kw);
    if (idx === -1) return false;

    // For multi-word keywords, indexOf is sufficient
    if (kw.indexOf(' ') !== -1) return true;

    // For single words, check boundaries to avoid partial matches
    var before = idx > 0 ? queryLower[idx - 1] : ' ';
    var after = idx + kw.length < queryLower.length ? queryLower[idx + kw.length] : ' ';
    var boundaryChars = ' \t\n.,;:!?()[]{}"\'-';
    return boundaryChars.indexOf(before) !== -1 && boundaryChars.indexOf(after) !== -1;
  }

  /**
   * Match yarn line collections against a query.
   * Returns array of { handle, title } objects, with priority matches first.
   */
  function matchYarnLines(query) {
    if (!query) return [];
    var lines = getYarnLines();
    var queryLower = query.toLowerCase();
    var prioritized = [];
    var normal = [];
    var seenHandles = {};

    lines.forEach(function(line) {
      if (seenHandles[line.handle]) return;
      var isPriority = false;
      var isMatch = false;

      line.keywords.forEach(function(kw) {
        if (isMatch) return;
        if (keywordMatches(queryLower, kw)) {
          isMatch = true;
          if (line.priority && line.priority.indexOf(kw) !== -1) {
            isPriority = true;
          }
        }
      });

      if (isMatch) {
        seenHandles[line.handle] = true;
        var entry = { handle: line.handle, title: line.title };
        if (isPriority) {
          prioritized.unshift(entry);
        } else {
          normal.push(entry);
        }
      }
    });

    return prioritized.concat(normal);
  }

  /**
   * Check if a collection title indicates a bundle collection.
   */
  function isBundleCollection(title) {
    return title && title.toLowerCase().indexOf('bundle') !== -1;
  }

  /**
   * Build HTML for yarn line collection links.
   * Used by both predictive search and full results page injection.
   */
  function buildYarnLineHTML(matches, style) {
    if (!matches.length) return '';

    if (style === 'predictive') {
      var html = '<div class="predictive-search__collection-suggestions" data-yarn-line-injected>';
      html += '<h2 class="predictive-search__heading">Collections</h2>';
      html += '<ul class="predictive-search__results-list" role="list">';
      matches.forEach(function(m) {
        html += '<li class="predictive-search__list-item" role="listitem">';
        html += '<a class="predictive-search__list-item-anchor" href="/collections/' + escapeAttr(m.handle) + '">';
        html += escapeHtml(m.title);
        html += '</a></li>';
      });
      html += '</ul></div>';
      return html;
    }

    // Full results page style
    var html = '<div class="one-whole column" data-yarn-line-injected>';
    html += '<h3 class="search-results__section-heading">Collections</h3>';
    html += '<ul class="search-results__collection-list">';
    matches.forEach(function(m) {
      html += '<li><a href="/collections/' + escapeAttr(m.handle) + '">' + escapeHtml(m.title) + '</a></li>';
    });
    html += '</ul></div>';
    return html;
  }

  /**
   * Inject yarn line matches into the predictive search dropdown.
   * Called after Shopify's Section Rendering API returns results.
   * Deduplicates against API-returned collections already in the DOM.
   */
  function injectPredictiveYarnLines(query, resultsContainer) {
    if (!resultsContainer) return;
    var matches = matchYarnLines(query);
    if (!matches.length) return;

    // Remove any previous injection
    var old = resultsContainer.querySelector('[data-yarn-line-injected]');
    if (old) old.remove();

    // Find API-returned collection handles already shown (via data attribute or href)
    var existingHandles = {};
    var existingLinks = resultsContainer.querySelectorAll('.predictive-search__collection-suggestions a[data-collection-handle]');
    existingLinks.forEach(function(link) {
      var handle = link.getAttribute('data-collection-handle');
      if (handle) existingHandles[handle] = true;
    });

    // Filter out duplicates
    var unique = matches.filter(function(m) { return !existingHandles[m.handle]; });
    if (!unique.length) return;

    var html = buildYarnLineHTML(unique, 'predictive');

    // Prepend to results
    var target = resultsContainer.querySelector('[data-predictive-search-results]') || resultsContainer;
    target.insertAdjacentHTML('afterbegin', html);
  }

  /**
   * Inject yarn line matches into the full search results page.
   * Runs on DOMContentLoaded for the search template.
   */
  function injectSearchPageYarnLines() {
    var section = document.querySelector('[data-section-type="search-template"]');
    if (!section) return;

    var container = section.closest('.shopify-section--search-template') || section.parentElement;
    var termsEl = container.querySelector('[data-search-terms]');
    if (!termsEl) return;

    var query = termsEl.getAttribute('data-search-terms');
    if (!query) return;

    var matches = matchYarnLines(query);

    if (matches.length) {
      var target = container.querySelector('[data-yarn-line-results]');
      if (target) {
        target.innerHTML = buildYarnLineHTML(matches, 'full');
      }
    }

    // Search analytics for full results page
    if (window.GistSearch && window.GistSearch.logSearch) {
      var enhanced = (window.GistSearch.enhance) ? window.GistSearch.enhance(query) : query;
      var countAttr = termsEl.getAttribute('data-search-results-count');
      var resultCount = countAttr ? parseInt(countAttr, 10) : null;

      window.GistSearch.logSearch({
        query: query,
        enhanced: enhanced,
        path: 'full_results',
        resultCount: isNaN(resultCount) ? null : resultCount,
        yarnLines: matches.map(function(m) { return m.handle; }).join(',')
      });
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Extend window.GistSearch (created by search-enhancer.js)
  var GS = window.GistSearch || {};
  GS.matchYarnLines = matchYarnLines;
  GS.isBundleCollection = isBundleCollection;
  GS.buildYarnLineHTML = buildYarnLineHTML;
  GS.injectPredictiveYarnLines = injectPredictiveYarnLines;
  GS.injectSearchPageYarnLines = injectSearchPageYarnLines;
  window.GistSearch = GS;

  // Auto-run on the full search results page
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectSearchPageYarnLines);
  } else {
    injectSearchPageYarnLines();
  }
})();
